import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * Shared investigation category tab bar (Corporate / Person / Email / Phone).
 * Hides zero-count chips (except All / active). Right-edge fade when overflow.
 * Optional ``mobileEssentialIds``: on &lt;sm show only those + active, rest under More.
 * Short viewports (max-height 480px): collapses to a compact Filter dropdown.
 *
 * @param {{
 *   filters: { id: string, label: string }[],
 *   activeFilter: string,
 *   onFilterChange: (id: string) => void,
 *   counts?: Record<string, number>,
 *   ariaLabel?: string,
 *   testId?: string,
 *   dangerFilterId?: string,
 *   hideZeroCounts?: boolean,
 *   mobileEssentialIds?: string[],
 * }} props
 */
export default function CategoryTabs({
  filters = [],
  activeFilter = 'all',
  onFilterChange,
  counts = {},
  ariaLabel = 'Result categories',
  testId = 'category-tabs',
  dangerFilterId = '',
  hideZeroCounts = false,
  mobileEssentialIds = null,
}) {
  const total = Number(counts.all) || 0;
  const scrollerRef = useRef(null);
  const moreRef = useRef(null);
  const menuRef = useRef(null);
  const shortMenuRef = useRef(null);
  const shortTriggerRef = useRef(null);
  const menuId = useId();
  const shortMenuId = useId();
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [shortOpen, setShortOpen] = useState(false);
  const [shortViewport, setShortViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(max-height: 480px)');
    const sync = () => setShortViewport(Boolean(mq.matches));
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  const afterZeroFilter = filters.filter((f) => {
    if (f.id === 'all') return true;
    if (activeFilter === f.id) return true;
    if (!hideZeroCounts) return true;
    const count = Number(counts[f.id]);
    if (!Number.isFinite(count)) return true;
    return count > 0;
  });

  const essentialSet = Array.isArray(mobileEssentialIds) && mobileEssentialIds.length
    ? new Set(mobileEssentialIds)
    : null;

  const primaryVisible = essentialSet
    ? afterZeroFilter.filter(
        (f) => essentialSet.has(f.id) || f.id === activeFilter || f.id === 'all',
      )
    : afterZeroFilter;

  const overflowFilters = essentialSet
    ? afterZeroFilter.filter(
        (f) => !essentialSet.has(f.id) && f.id !== 'all' && f.id !== activeFilter,
      )
    : [];

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || shortViewport) return undefined;
    const measure = () => {
      setCanScrollRight(
        el.scrollWidth > el.clientWidth + 4 &&
          el.scrollLeft < el.scrollWidth - el.clientWidth - 4,
      );
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [filters, counts, activeFilter, moreOpen, shortViewport]);

  useEffect(() => {
    if (!moreOpen && !shortOpen) return undefined;
    const onDoc = (e) => {
      if (
        moreRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target) ||
        shortTriggerRef.current?.contains(e.target) ||
        shortMenuRef.current?.contains(e.target)
      ) {
        return;
      }
      setMoreOpen(false);
      setShortOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMoreOpen(false);
        setShortOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen, shortOpen]);

  const activeMeta = afterZeroFilter.find((f) => f.id === activeFilter) || afterZeroFilter[0];
  const activeCount =
    activeFilter === 'all' ? total : Number(counts[activeFilter]);
  const shortLabel =
    activeFilter === 'all'
      ? `Filter: All (${total})`
      : `Filter: ${activeMeta?.label || activeFilter}${
          Number.isFinite(activeCount) ? ` (${activeCount})` : ''
        }`;

  const renderChip = (f, { desktopOnly = false, mobileOnly = false, compact = false } = {}) => {
    const isActive = activeFilter === f.id;
    const count = f.id === 'all' ? total : counts[f.id];
    const isDanger = Boolean(dangerFilterId) && f.id === dangerFilterId;
    const showCount = typeof count === 'number';
    const visibility =
      desktopOnly ? 'hidden sm:inline-flex' : mobileOnly ? 'inline-flex sm:hidden' : 'inline-flex';
    const size = compact
      ? 'h-7 items-center gap-1 rounded-full border px-2 py-0 text-[10px] font-bold uppercase tracking-wide'
      : 'items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide sm:px-3.5 sm:py-1.5 sm:text-xs sm:tracking-wider';
    return (
      <button
        key={f.id}
        type="button"
        onClick={() => {
          onFilterChange?.(f.id);
          setMoreOpen(false);
          setShortOpen(false);
        }}
        aria-pressed={isActive}
        data-testid={isDanger ? `corp-filter-${f.id.replace(/_/g, '-')}` : undefined}
        className={`${visibility} shrink-0 transition ${size} ${
          isActive
            ? isDanger
              ? 'border-rose-400/55 bg-rose-500/15 text-rose-100 shadow-[0_0_12px_rgba(244,63,94,0.2)]'
              : 'border-[#00E5FF]/70 bg-[#00E5FF]/20 text-[#00E5FF] shadow-[0_0_14px_rgba(0,229,255,0.28)] ring-1 ring-[#00E5FF]/35'
            : 'border-white/15 bg-slate-900/70 text-slate-300 hover:border-white/30 hover:text-slate-100'
        }`}
      >
        {f.id === 'all' ? `All (${total})` : f.label}
        {f.id !== 'all' && showCount ? (
          <span
            className={`tabular-nums ${
              isActive
                ? isDanger
                  ? 'text-rose-200'
                  : 'text-[#00E5FF]'
                : isDanger && count > 0
                  ? 'text-rose-400'
                  : 'text-slate-400'
            }`}
          >
            {count}
          </span>
        ) : null}
      </button>
    );
  };

  if (shortViewport) {
    return (
      <div className="relative mb-1.5" data-testid={`${testId}-wrap`} data-compact="short-viewport">
        <div className="relative inline-flex max-w-full" ref={shortTriggerRef}>
          <button
            type="button"
            onClick={() => setShortOpen((o) => !o)}
            aria-expanded={shortOpen}
            aria-controls={shortOpen ? shortMenuId : undefined}
            aria-haspopup="listbox"
            aria-label={ariaLabel}
            className="inline-flex h-7 max-w-full items-center gap-1.5 truncate rounded-md border border-white/20 bg-slate-900/90 px-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-100"
            data-testid={`${testId}-short-trigger`}
          >
            <span className="truncate">{shortLabel}</span>
            <span aria-hidden className="shrink-0 text-slate-400">
              {shortOpen ? '▴' : '▾'}
            </span>
          </button>
          {shortOpen ? (
            <ul
              ref={shortMenuRef}
              id={shortMenuId}
              role="listbox"
              className="absolute left-0 top-full z-40 mt-1 max-h-[min(50vh,14rem)] min-w-[12rem] overflow-y-auto rounded-lg border border-slate-600/80 bg-slate-950/95 p-1 shadow-xl backdrop-blur-md"
              data-testid={`${testId}-short-menu`}
            >
              {afterZeroFilter.map((f) => {
                const count = f.id === 'all' ? total : counts[f.id];
                const isActive = activeFilter === f.id;
                return (
                  <li key={f.id} role="option" aria-selected={isActive}>
                    <button
                      type="button"
                      className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-white/10 ${
                        isActive ? 'bg-cyan-500/15 text-cyan-100' : 'text-slate-300 hover:text-white'
                      }`}
                      onClick={() => {
                        onFilterChange?.(f.id);
                        setShortOpen(false);
                      }}
                      data-testid={
                        dangerFilterId && f.id === dangerFilterId
                          ? `corp-filter-${f.id.replace(/_/g, '-')}`
                          : undefined
                      }
                    >
                      <span>{f.id === 'all' ? `All (${total})` : f.label}</span>
                      {f.id !== 'all' && typeof count === 'number' ? (
                        <span className="tabular-nums text-slate-500">{count}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-2 sm:mb-3" data-testid={`${testId}-wrap`}>
      <nav
        ref={scrollerRef}
        className="flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {essentialSet
          ? primaryVisible.map((f) => renderChip(f, { mobileOnly: false }))
          : afterZeroFilter.map((f) => renderChip(f))}

        {essentialSet
          ? overflowFilters.map((f) => renderChip(f, { desktopOnly: true }))
          : null}

        {essentialSet && overflowFilters.length > 0 ? (
          <div className="relative shrink-0 sm:hidden" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={moreOpen}
              aria-controls={moreOpen ? menuId : undefined}
              aria-haspopup="listbox"
              className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-slate-900/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-300"
              data-testid={`${testId}-more`}
            >
              More
              <span aria-hidden>{moreOpen ? '▴' : '▾'}</span>
            </button>
            {moreOpen ? (
              <ul
                ref={menuRef}
                id={menuId}
                role="listbox"
                className="absolute right-0 top-full z-40 mt-1 min-w-[10rem] rounded-lg border border-slate-600/80 bg-slate-950/95 p-1 shadow-xl backdrop-blur-md"
                data-testid={`${testId}-more-menu`}
              >
                {overflowFilters.map((f) => {
                  const count = counts[f.id];
                  return (
                    <li key={f.id} role="option" aria-selected={activeFilter === f.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[11px] font-semibold text-slate-300 hover:bg-white/10 hover:text-white"
                        onClick={() => {
                          onFilterChange?.(f.id);
                          setMoreOpen(false);
                        }}
                      >
                        <span>{f.label}</span>
                        {typeof count === 'number' ? (
                          <span className="tabular-nums text-slate-500">{count}</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
      </nav>
      {canScrollRight ? (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-slate-950 via-slate-950/80 to-transparent"
          aria-hidden
          data-testid={`${testId}-scroll-fade`}
        />
      ) : null}
    </div>
  );
}
